import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { BookPlus, Loader2, Search, Trash2, ArrowDownCircle, ArrowUpCircle, RefreshCw, CheckCircle2, History, ScrollText, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MODES = ["Cash", "Bank", "UPI", "Cheque", "Adjustment", "Other"];

const fmtINR = (v) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(v || 0);

export const CrDrEntriesPanel = () => {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "super_admin";

  const [clients, setClients] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // form
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    client_code: "",
    entry_type: "credit",
    amount: "",
    mode: "Bank",
    narration: "",
  });

  // filters
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // approval queue + audit
  const [pendingCount, setPendingCount] = useState(0);
  const [approvingId, setApprovingId] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState("");
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/ledgers`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      const list = (Array.isArray(d) ? d : d.ledgers || [])
        .map((l) => ({ code: l.client_code, name: l.name || l.client_name || l.client_code }))
        .filter((c) => c.code)
        .sort((a, b) => a.code.localeCompare(b.code));
      setClients(list);
    } catch {}
  }, [token]);

  const fetchPendingCount = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/crdr-entries?status=pending&limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) setPendingCount((d.entries || []).length);
    } catch {}
  }, [token]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (typeFilter !== "all") params.set("entry_type", typeFilter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await fetch(`${BACKEND_URL}/api/crdr-entries?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (res.ok) setEntries(d.entries || []);
    } catch {} finally {
      setLoading(false);
    }
  }, [token, search, typeFilter, statusFilter, dateFrom, dateTo]);

  useEffect(() => { fetchClients(); fetchPendingCount(); }, [fetchClients, fetchPendingCount]);
  useEffect(() => {
    const t = setTimeout(fetchEntries, 350);
    return () => clearTimeout(t);
  }, [fetchEntries]);

  const submit = async () => {
    if (!form.date) return toast.error("Date is required");
    if (!form.client_code) return toast.error("Client selection is mandatory");
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Amount must be greater than 0");
    if (!form.narration.trim()) return toast.error("Narration is mandatory");
    if (saving) return; // double-click guard
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/crdr-entries`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          amount: amt,
          submission_key: `${form.client_code}|${form.date}|${form.entry_type}|${amt}|${Date.now()}`,
        }),
      });
      const d = await res.json();
      if (!res.ok) {
        toast.error(typeof d.detail === "string" ? d.detail : "Failed to save entry");
        return;
      }
      toast.success("Cr/Dr entry recorded successfully");
      setForm((f) => ({ ...f, amount: "", narration: "" }));
      fetchEntries();
      fetchPendingCount();
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/crdr-entries/${deleteTarget.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Delete failed"); return; }
      toast.success("Entry deleted");
      setDeleteTarget(null);
      fetchEntries();
      fetchPendingCount();
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setDeleting(false);
    }
  };

  const approveEntry = async (entry) => {
    if (approvingId) return;
    setApprovingId(entry.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/crdr-entries/${entry.id}/approve`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await res.json();
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Approve failed"); return; }
      toast.success(`${entry.client_code} ${entry.entry_type} approved — balances updated`);
      fetchEntries();
      fetchPendingCount();
    } catch (e) {
      toast.error("Network error: " + e.message);
    } finally {
      setApprovingId(null);
    }
  };

  const openAudit = async (entry) => {
    setAuditTitle(entry ? `Audit Trail — ${entry.client_code} ${fmtINR(entry.amount)} (${entry.entry_type})` : "Cr/Dr Audit Log");
    setAuditOpen(true);
    setAuditLoading(true);
    setAuditEvents([]);
    try {
      const url = entry
        ? `${BACKEND_URL}/api/crdr-entries/${entry.id}/audit`
        : `${BACKEND_URL}/api/crdr-audit?limit=100`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      if (res.ok) setAuditEvents(d.events || []);
    } catch {} finally {
      setAuditLoading(false);
    }
  };

  const totals = entries.reduce(
    (acc, e) => {
      if (e.entry_type === "credit") acc.credit += e.amount || 0;
      else acc.debit += e.amount || 0;
      return acc;
    },
    { credit: 0, debit: 0 }
  );

  // Per-client monthly Cr/Dr net summary (from the filtered entries)
  const monthlySummary = (() => {
    const map = new Map();
    entries.forEach((e) => {
      const ym = (e.date || "").slice(0, 7); // YYYY-MM
      if (!ym) return;
      const key = `${e.client_code}|${ym}`;
      if (!map.has(key)) map.set(key, { client: e.client_code, ym, credit: 0, debit: 0 });
      const g = map.get(key);
      if (e.entry_type === "credit") g.credit += e.amount || 0;
      else g.debit += e.amount || 0;
    });
    return [...map.values()]
      .map((g) => ({ ...g, net: g.credit - g.debit }))
      .sort((a, b) => b.ym.localeCompare(a.ym) || a.client.localeCompare(b.client));
  })();

  const fmtYM = (ym) => {
    const [y, m] = ym.split("-");
    return `${["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][Number(m)] || m} ${y}`;
  };

  const exportExcel = () => {
    if (entries.length === 0) return toast.error("No entries to export");
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const headers = ["Date", "Client", "Credit (₹)", "Debit (₹)", "Mode", "Narration", "Status", "Created By", "Entry Time"];
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n<?mso-application progid="Excel.Sheet"?>\n';
    xml += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">\n';
    xml += '<Styles><Style ss:ID="header"><Font ss:Bold="1" ss:Size="11"/><Interior ss:Color="#E0F2F1" ss:Pattern="Solid"/></Style>';
    xml += '<Style ss:ID="cr"><Font ss:Color="#047857"/><NumberFormat ss:Format="#,##0.00"/></Style>';
    xml += '<Style ss:ID="dr"><Font ss:Color="#B91C1C"/><NumberFormat ss:Format="#,##0.00"/></Style>';
    xml += '<Style ss:ID="total"><Font ss:Bold="1"/><Interior ss:Color="#F1F5F9" ss:Pattern="Solid"/><NumberFormat ss:Format="#,##0.00"/></Style></Styles>\n';
    xml += '<Worksheet ss:Name="CrDr Statement">\n<Table>\n<Row>';
    headers.forEach((h) => { xml += `<Cell ss:StyleID="header"><Data ss:Type="String">${esc(h)}</Data></Cell>`; });
    xml += '</Row>\n';
    entries.forEach((e) => {
      const entryTime = e.created_at
        ? new Date(e.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
        : "";
      xml += '<Row>';
      xml += `<Cell><Data ss:Type="String">${esc(e.date)}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${esc(e.client_code)}</Data></Cell>`;
      xml += e.entry_type === "credit"
        ? `<Cell ss:StyleID="cr"><Data ss:Type="Number">${e.amount || 0}</Data></Cell><Cell><Data ss:Type="String"></Data></Cell>`
        : `<Cell><Data ss:Type="String"></Data></Cell><Cell ss:StyleID="dr"><Data ss:Type="Number">${e.amount || 0}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${esc(e.mode)}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${esc(e.narration)}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${esc(e.status)}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${esc(e.created_by)}</Data></Cell>`;
      xml += `<Cell><Data ss:Type="String">${esc(entryTime)}</Data></Cell>`;
      xml += '</Row>\n';
    });
    xml += '<Row>';
    xml += '<Cell ss:StyleID="total"><Data ss:Type="String">TOTAL</Data></Cell><Cell ss:StyleID="total"><Data ss:Type="String"></Data></Cell>';
    xml += `<Cell ss:StyleID="total"><Data ss:Type="Number">${totals.credit}</Data></Cell>`;
    xml += `<Cell ss:StyleID="total"><Data ss:Type="Number">${totals.debit}</Data></Cell>`;
    for (let i = 0; i < 5; i++) xml += '<Cell ss:StyleID="total"><Data ss:Type="String"></Data></Cell>';
    xml += '</Row>\n</Table>\n</Worksheet>\n</Workbook>';
    const blob = new Blob([xml], { type: "application/vnd.ms-excel" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const parts = ["crdr_statement"];
    if (typeFilter !== "all") parts.push(typeFilter);
    if (statusFilter !== "all") parts.push(statusFilter);
    if (dateFrom) parts.push(`from_${dateFrom}`);
    if (dateTo) parts.push(`to_${dateTo}`);
    a.download = `${parts.join("_")}.xls`;
    a.click();
    toast.success(`Exported ${entries.length} entries to Excel`);
  };


  return (
    <div className="space-y-4" data-testid="crdr-panel">
      {/* Entry Form */}
      <Card className="border-teal-200">
        <CardHeader className="py-3 px-4 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-t-lg">
          <CardTitle className="text-base flex items-center gap-2">
            <BookPlus className="w-4 h-4 text-teal-600" />
            New Cr/Dr Entry
            <span className="text-xs font-normal text-slate-500 ml-1">
              Manual accounting adjustment — reflects in Ledger, Reconciliation &amp; Financial Overview
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                className="h-9 mt-1" data-testid="crdr-date-input" />
            </div>
            <div>
              <Label className="text-xs">Client *</Label>
              <Select value={form.client_code} onValueChange={(v) => setForm((f) => ({ ...f, client_code: v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="crdr-client-select">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {clients.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Entry Type *</Label>
              <div className="flex gap-1 mt-1">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, entry_type: "credit" }))}
                  className={`flex-1 h-9 rounded-md text-xs font-semibold border transition-colors ${
                    form.entry_type === "credit"
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  }`}
                  data-testid="crdr-type-credit"
                >
                  Credit
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, entry_type: "debit" }))}
                  className={`flex-1 h-9 rounded-md text-xs font-semibold border transition-colors ${
                    form.entry_type === "debit"
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-white text-red-700 border-red-300 hover:bg-red-50"
                  }`}
                  data-testid="crdr-type-debit"
                >
                  Debit
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Amount (₹) *</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                className="h-9 mt-1" data-testid="crdr-amount-input" />
            </div>
            <div>
              <Label className="text-xs">Mode *</Label>
              <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="crdr-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={submit}
                disabled={saving}
                className={`w-full h-9 ${form.entry_type === "credit" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
                data-testid="crdr-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Save ${form.entry_type === "credit" ? "Credit" : "Debit"}`}
              </Button>
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs">Narration *</Label>
            <Textarea
              placeholder="Reason / reference for this entry..."
              value={form.narration}
              onChange={(e) => setForm((f) => ({ ...f, narration: e.target.value }))}
              className="mt-1 min-h-[60px] text-sm"
              data-testid="crdr-narration-input"
            />
          </div>
          {!isAdmin && (
            <p className="text-[11px] text-amber-600 mt-2">Non-admin entries require admin approval before affecting balances.</p>
          )}
        </CardContent>
      </Card>

      {/* Monthly per-client Cr/Dr net summary strip */}
      {monthlySummary.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1.5 -mb-1" data-testid="crdr-monthly-summary-strip">
          {monthlySummary.map((g) => {
            const [yy, mm] = g.ym.split("-").map(Number);
            const monthStart = `${g.ym}-01`;
            const monthEnd = `${g.ym}-${String(new Date(yy, mm, 0).getDate()).padStart(2, "0")}`;
            const isActive = search === g.client && dateFrom === monthStart && dateTo === monthEnd;
            return (
            <button
              type="button"
              key={`${g.client}|${g.ym}`}
              onClick={() => {
                if (isActive) {
                  setSearch(""); setDateFrom(""); setDateTo("");
                } else {
                  setSearch(g.client); setDateFrom(monthStart); setDateTo(monthEnd);
                }
              }}
              title={isActive ? "Click to clear this statement filter" : `Show ${g.client} statement for ${fmtYM(g.ym)}`}
              className={`flex-shrink-0 min-w-[168px] text-left rounded-lg border px-3 py-2 shadow-sm transition-colors cursor-pointer ${
                isActive ? "bg-teal-50 border-teal-400 ring-1 ring-teal-300" : "bg-white hover:bg-slate-50 hover:border-slate-300"
              }`}
              data-testid={`crdr-summary-${g.client}-${g.ym}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-bold text-slate-800 truncate max-w-[100px]" title={g.client}>{g.client}</span>
                <span className="text-[10px] text-slate-400 whitespace-nowrap">{fmtYM(g.ym)}</span>
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-[11px] text-emerald-700 font-semibold">Cr {fmtINR(g.credit)}</span>
                <span className="text-[11px] text-red-700 font-semibold">Dr {fmtINR(g.debit)}</span>
              </div>
              <div className={`text-xs font-bold mt-0.5 ${g.net >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                Net {g.net >= 0 ? "+" : "−"}{fmtINR(Math.abs(g.net))}
              </div>
            </button>
            );
          })}
        </div>
      )}

      {/* Filters + Table */}
      <Card>
        <CardHeader className="py-3 px-4">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-base mr-auto" data-testid="crdr-count">
              Cr/Dr Transactions ({entries.length})
            </CardTitle>
            {isAdmin && pendingCount > 0 && (
              <Button
                variant={statusFilter === "pending" ? "default" : "outline"}
                size="sm"
                className={`h-8 text-xs gap-1 ${statusFilter === "pending" ? "bg-amber-600 hover:bg-amber-700" : "text-amber-700 border-amber-300 hover:bg-amber-50"}`}
                onClick={() => setStatusFilter(statusFilter === "pending" ? "all" : "pending")}
                data-testid="crdr-pending-queue-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                {pendingCount} Pending Approval
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-green-700 border-green-300 hover:bg-green-50" onClick={exportExcel} data-testid="crdr-export-btn">
              <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-slate-600" onClick={() => openAudit(null)} data-testid="crdr-audit-log-btn">
              <ScrollText className="w-3.5 h-3.5" /> Audit Log
            </Button>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search client / narration..." value={search} onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-8 w-[200px] text-xs" data-testid="crdr-search-input" />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="h-8 w-[110px] text-xs" data-testid="crdr-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="credit">Credit</SelectItem>
                <SelectItem value="debit">Debit</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-[115px] text-xs" data-testid="crdr-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 w-[135px] text-xs" data-testid="crdr-date-from" />
            <span className="text-xs text-slate-400">to</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 w-[135px] text-xs" data-testid="crdr-date-to" />
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchEntries} data-testid="crdr-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
          <div className="flex gap-4 mt-1 text-xs">
            <span className="flex items-center gap-1 text-emerald-700 font-medium">
              <ArrowDownCircle className="w-3.5 h-3.5" /> Credits: {fmtINR(totals.credit)}
            </span>
            <span className="flex items-center gap-1 text-red-700 font-medium">
              <ArrowUpCircle className="w-3.5 h-3.5" /> Debits: {fmtINR(totals.debit)}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[520px]">
            <Table data-testid="crdr-table">
              <TableHeader className="sticky top-0 z-10 bg-slate-50">
                <TableRow>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Client</TableHead>
                  <TableHead className="text-xs text-right text-emerald-700">Credit</TableHead>
                  <TableHead className="text-xs text-right text-red-700">Debit</TableHead>
                  <TableHead className="text-xs">Mode</TableHead>
                  <TableHead className="text-xs min-w-[180px]">Narration</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Created By</TableHead>
                  <TableHead className="text-xs whitespace-nowrap">Entry Time</TableHead>
                  <TableHead className="text-xs w-24"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-10 text-slate-400 text-sm">
                      {loading ? "Loading..." : "No Cr/Dr entries found"}
                    </TableCell>
                  </TableRow>
                ) : entries.map((e) => (
                  <TableRow key={e.id} className={e.entry_type === "credit" ? "bg-emerald-50/30" : "bg-red-50/20"} data-testid={`crdr-row-${e.id}`}>
                    <TableCell className="text-xs whitespace-nowrap">{e.date}</TableCell>
                    <TableCell className="text-xs font-semibold">{e.client_code}</TableCell>
                    <TableCell className="text-xs text-right font-bold text-emerald-700">
                      {e.entry_type === "credit" ? fmtINR(e.amount) : "-"}
                    </TableCell>
                    <TableCell className="text-xs text-right font-bold text-red-700">
                      {e.entry_type === "debit" ? fmtINR(e.amount) : "-"}
                    </TableCell>
                    <TableCell className="text-xs">{e.mode}</TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[260px] truncate" title={e.narration}>{e.narration}</TableCell>
                    <TableCell>
                      <Badge className={`text-[9px] px-1.5 py-0 ${e.status === "approved" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"}`}>
                        {e.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{e.created_by}</TableCell>
                    <TableCell className="text-xs text-slate-400 whitespace-nowrap">
                      {e.created_at ? new Date(e.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-0.5">
                        {isAdmin && e.status === "pending" && (
                          <Button size="sm" className="h-7 px-2 text-[10px] bg-amber-600 hover:bg-amber-700 gap-1"
                            disabled={approvingId === e.id}
                            onClick={() => approveEntry(e)} data-testid={`crdr-approve-${e.id}`}>
                            {approvingId === e.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                            Approve
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-slate-700"
                          onClick={() => openAudit(e)} title="Audit trail" data-testid={`crdr-history-${e.id}`}>
                          <History className="w-3.5 h-3.5" />
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteTarget(e)} data-testid={`crdr-delete-${e.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Audit Trail Dialog */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-lg max-h-[75vh] overflow-y-auto" data-testid="crdr-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <History className="w-4 h-4 text-slate-500" /> {auditTitle}
            </DialogTitle>
          </DialogHeader>
          {auditLoading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>
          ) : auditEvents.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-400">No audit events recorded for this entry</p>
          ) : (
            <div className="space-y-2">
              {auditEvents.map((ev) => (
                <div key={ev.id} className="flex gap-3 p-2.5 rounded-lg border bg-slate-50/50" data-testid={`crdr-audit-event-${ev.id}`}>
                  <Badge className={`self-start mt-0.5 text-[9px] px-1.5 py-0 ${
                    ev.action === "created" ? "bg-blue-100 text-blue-700 hover:bg-blue-100" :
                    ev.action === "approved" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" :
                    "bg-red-100 text-red-700 hover:bg-red-100"
                  }`}>{ev.action}</Badge>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-700">{ev.details}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      by <span className="font-medium">{ev.by}</span> ({ev.role}) • {ev.at ? new Date(ev.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="crdr-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Cr/Dr Entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>This will permanently remove the <strong className={deleteTarget.entry_type === "credit" ? "text-emerald-700" : "text-red-700"}>{deleteTarget.entry_type}</strong> entry
                of <strong>{fmtINR(deleteTarget.amount)}</strong> for <strong>{deleteTarget.client_code}</strong> and update all ledger balances. This cannot be undone.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700" data-testid="crdr-delete-confirm">
              {deleting ? "Deleting..." : "Delete Entry"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CrDrEntriesPanel;
